import React, { useState, memo } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { ProgramDetailModal } from '../components/ui/ProgramDetailModal';
import { CheckIcon, DumbbellIcon, UsersIcon, TrendingUpIcon, HeartIcon, AwardIcon, DollarSignIcon, CalendarIcon, MapPinIcon, StarIcon, VideoIcon, BuildingIcon, GlobeIcon, ClipboardCheckIcon, BookOpenIcon, HandshakeIcon, SparklesIcon, XIcon } from 'lucide-react';
export function TrainerPartnerProgram() {
  const [activePersona, setActivePersona] = useState('inperson');
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationData, setApplicationData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    businessType: '',
    clientBase: '',
    website: '',
    instagram: '',
    experience: '',
    message: ''
  });
  const handleApplicationChange = e => {
    const {
      name,
      value
    } = e.target;
    setApplicationData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleApplicationSubmit = e => {
    e.preventDefault();
    // Form submission logic would go here
    alert('Thank you for your application! We will review it and contact you within 48 hours.');
    setShowApplicationForm(false);
    setApplicationData({
      name: '',
      email: '',
      phone: '',
      businessName: '',
      businessType: '',
      clientBase: '',
      website: '',
      instagram: '',
      experience: '',
      message: ''
    });
  };
  const personas = [{
    id: 'inperson',
    title: 'In-Person Trainers',
    icon: <DumbbellIcon className="h-6 w-6 text-[#0099A1]" data-id="element-2889" />,
    description: 'Personal trainers and independent fitness coaches'
  }, {
    id: 'virtual',
    title: 'Virtual Trainers',
    icon: <VideoIcon className="h-6 w-6 text-[#0099A1]" data-id="element-2890" />,
    description: 'Online coaches and digital fitness professionals'
  }, {
    id: 'influencers',
    title: 'Wellness Influencers',
    icon: <SparklesIcon className="h-6 w-6 text-[#0099A1]" data-id="element-2891" />,
    description: 'Content creators and wellness advocates'
  }, {
    id: 'clubs',
    title: 'Health Clubs',
    icon: <BuildingIcon className="h-6 w-6 text-[#0099A1]" data-id="element-2892" />,
    description: 'Gyms, studios, and fitness facilities'
  }];
  const personaContent = {
    inperson: {
      title: 'In-Person Personal Trainers',
      subtitle: 'Elevate Your Training Business with Destination Experiences',
      benefits: [{
        title: 'Increase Client Lifetime Value',
        description: 'Transform one-time training sessions into high-value retreat experiences that boost retention and referrals.',
        icon: <TrendingUpIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2893" />
      }, {
        title: 'Build Your Premium Brand',
        description: 'Position yourself as a premium trainer who offers transformative experiences, not just workout sessions.',
        icon: <AwardIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2894" />
      }, {
        title: 'Create Group Training Opportunities',
        description: 'Bring multiple clients together for group retreats, increasing your efficiency and earning potential.',
        icon: <UsersIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2895" />
      }, {
        title: 'Monetize your Passion Further',
        description: 'Create a new revenue stream within the same space and same clientele, without diverting from your career of interest.',
        icon: <BookOpenIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2896" />
      }],
      challenges: ['Limited earning potential with hourly training', 'Client retention and engagement challenges', 'Competing with big-box gym offerings', 'Seasonal business fluctuations'],
      solutions: ['High-value retreat packages', 'Exclusive client experiences', 'Premium positioning opportunity', 'Year-round booking opportunities']
    },
    virtual: {
      title: 'Virtual Trainers & Online Coaches',
      subtitle: 'Transform Your Digital Community into Real-World Results',
      benefits: [{
        title: 'Deepen Client Relationships',
        description: 'Take your virtual coaching relationship offline with immersive wellness experiences that strengthen bonds and increase retention.',
        icon: <HeartIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2897" />
      }, {
        title: 'Differentiate Your Offering',
        description: 'Stand out in the crowded online fitness space by offering exclusive retreat experiences your competitors cannot match.',
        icon: <StarIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2898" />
      }, {
        title: 'Generate Additional Revenue',
        description: 'Earn competitive commissions on every client booking while providing exceptional value to your community.',
        icon: <DollarSignIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2899" />
      }, {
        title: 'Zero Planning Burden',
        description: 'We handle all logistics, programming, and execution. You focus on your clients while we deliver the experience.',
        icon: <ClipboardCheckIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2900" />
      }],
      challenges: ['Building deeper connections with virtual-only clients', 'Differentiating from other online trainers', 'Creating memorable experiences beyond the screen', 'Generating passive income streams'],
      solutions: ['Exclusive in-person retreat experiences', 'Unique value proposition for your brand', 'Turnkey program management', 'Competitive revenue structure']
    },
    influencers: {
      title: 'Wellness Influencers & Content Creators',
      subtitle: 'Monetize Your Influence with Exclusive Wellness Experiences',
      benefits: [{
        title: 'Authentic Brand Partnerships',
        description: 'Offer your audience genuine, transformative experiences that align with your wellness values and strengthen community trust.',
        icon: <HeartIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2901" />
      }, {
        title: 'Premium Content Opportunities',
        description: 'Create compelling content from stunning locations while providing real value to your followers through exclusive retreat experiences.',
        icon: <SparklesIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2902" />
      }, {
        title: 'Diversified Revenue Streams',
        description: 'Generate income beyond sponsorships and ads with high-ticket retreat offerings and attractive commission structures.',
        icon: <DollarSignIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2903" />
      }, {
        title: 'Community Building',
        description: 'Bring your online community together for in-person connections that deepen engagement and loyalty to your brand.',
        icon: <UsersIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2904" />
      }],
      challenges: ['Monetizing influence beyond traditional sponsorships', 'Creating meaningful connections with followers', 'Standing out in saturated wellness space', 'Balancing content creation with business growth'],
      solutions: ['High-value experiential offerings', 'In-person community experiences', 'Exclusive partnership opportunities', 'Turnkey retreat management']
    },
    clubs: {
      title: 'Health Clubs & Fitness Studios',
      subtitle: 'Enhance Member Value with Exclusive Wellness Retreats',
      benefits: [{
        title: 'Member Retention Tool',
        description: 'Offer exclusive retreat experiences that increase member loyalty and reduce churn rates.',
        icon: <HeartIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2905" />
      }, {
        title: 'Premium Membership Tier',
        description: 'Create a high-value membership tier that includes access to annual wellness retreats.',
        icon: <StarIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2906" />
      }, {
        title: 'Staff Incentive Program',
        description: 'Reward top-performing trainers and staff with complimentary retreat attendance.',
        icon: <AwardIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2907" />
      }, {
        title: 'Corporate Partnership Opportunities',
        description: 'Package retreats as corporate wellness offerings for your business clients.',
        icon: <HandshakeIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2908" />
      }],
      challenges: ['Member retention in competitive markets', 'Differentiating from other local clubs', 'Maximizing membership revenue', 'Staff motivation and development'],
      solutions: ['Exclusive member benefits', 'Unique value proposition', 'Additional revenue streams', 'Staff recognition programs']
    }
  };
  // Featured programs from ComebackPrograms
  const programs = [{
    id: 'boxing-bootcamp',
    title: 'Boxing & Fitness Bootcamp with Chris Clements',
    category: 'Fitness',
    ideal: 'Athletes, fitness enthusiasts, boxing beginners',
    description: 'Train like a champion with professional boxer Chris Clements in an intensive boxing and fitness bootcamp designed to push your limits and transform your mindset.',
    duration: '4 days, 3 nights',
    price: 'Starting at $1,599',
    priceValue: 1599,
    groupSize: '15-25 participants',
    dates: ['Jan 26-29, 2026'],
    location: 'Hard Rock Hotel Cancun, Mexico',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9avHThGYydRCkXZU4SoNUQ/Boxing.webp',
    hostImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9jNa6v9iwd3jG5mHFF8SXo/Chris2.jpg',
    hostName: 'Chris Clements',
    hostBio: 'Professional boxer and fitness coach with over 15 years of experience training athletes at all levels.',
    highlights: ['Daily boxing training sessions with pro fighters & elite coaches', 'High-intensity interval training (HIIT) and fight-conditioning workouts', 'Mobility, recovery, and breathwork sessions to keep you performing', 'Fighter mindset & "Fight IQ" coaching (film breakdowns, strategy, psychology)', 'All-inclusive Hotel accommodations (double occupancy)', 'Small-group pods for community, accountability, and friendly competition', 'Group sparring / technical drills and padwork refinement (level-based & optional)', 'Professional photo/video content capture of your training experience', 'Airport transfers included', 'Flights handled by participants – use your airline points and travel in style']
  }, {
    id: 'wellness-reset',
    title: 'Wellness Reset: Yoga & Breathwork',
    category: 'Wellness',
    ideal: 'Wellness seekers, yoga practitioners, stress relief',
    description: 'Rejuvenate your mind, body, and spirit with a transformative wellness retreat focused on yoga and breathwork practices.',
    duration: '4 days, 3 nights',
    price: 'Starting at $1,599',
    priceValue: 1599,
    groupSize: '15-20 participants',
    dates: ['End of February 2026', 'End of April 2026'],
    location: 'Cancun/Riviera Maya',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/o1fjDw612HCk6Xa8RMhNyh/Yoga.webp',
    hostImage: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=987&q=80',
    hostName: 'Maria Rodriguez',
    hostBio: 'Certified yoga instructor and breathwork specialist with 10+ years of experience guiding transformative wellness retreats.',
    highlights: ['Twice-daily yoga practices (flow, Yin/Restorative, and gentle embodied movement)', 'Guided breathwork sessions for calming, releasing, and resetting your nervous system', 'Nervous system education: stress, regulation, and everyday tools you can use at home', 'Intention-setting, reflection circles, and Soul Pods for deeper community connection', 'All-inclusive Hard Rock Hotel accommodations (double occupancy)', 'Sunrise or sunset practices in breathtaking resort settings', 'Post-retreat digital bundle with recorded practices & integration tools', 'Airport transfers included', 'Flights handled by participants – use your airline points and travel in style']
  }, {
    id: 'nutrition-wellness',
    title: 'Nutrition & Wellness Transformation',
    category: 'Wellness',
    ideal: 'Health-conscious individuals, nutrition enthusiasts',
    description: 'Transform your relationship with food and discover sustainable nutrition strategies with expert guidance in a stunning beach setting.',
    duration: '4 days, 3 nights',
    price: 'Starting at $1,599',
    priceValue: 1599,
    groupSize: '15-20 participants',
    dates: ['March 8-11, 2026', 'Sept 20-23, 2026', 'Nov 15-18, 2026'],
    location: 'Tulum, Mexico',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/aEgUSDVN3SaRCTgZHE6TX2/Nutrition.webp',
    hostImage: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    hostName: 'Dr. Sarah Chen',
    hostBio: 'Registered dietitian and certified nutrition specialist with expertise in sustainable lifestyle transformation and mindful eating practices.',
    highlights: ['Daily workshops on metabolism, hormones, cravings, and sustainable fat loss', 'Guided meals showing you exactly how to build plates for energy and weight loss', 'Emotional and habit-based eating sessions to tackle stress and late-night snacking', 'Light daily movement sessions (walks, low-impact workouts, mobility)', 'All-inclusive Hard Rock Hotel accommodations (double occupancy)', 'Accountability Pods for support, check-ins, and real-life strategy sharing', 'Personalized 6-week post-retreat nutrition & habit blueprint', 'Airport transfers included', 'Flights handled by participants – use your airline points and travel in style']
  }];
  const commissionStructure = [{
    tier: 'Bronze Partner',
    bookings: 'up to 10 clients/year',
    commission: 'up to $100/client',
    benefits: ['New Income Opportunity for your facility', 'Brand Awareness', 'Increase in Client retention', 'Marketing Materials', 'Partner Portal access', 'Recruitment education sessions']
  }, {
    tier: 'Silver Partner',
    bookings: 'up to 25 clients/year',
    commission: 'up to $125/client',
    benefits: ['New Income Opportunity for your facility', 'Brand Awareness', 'Increase in Client retention', 'Co-branded Marketing Materials', 'Partner Portal access', 'Recruitment education sessions', 'Dedicated Account Manager']
  }, {
    tier: 'Gold Partner',
    bookings: '25+ clients/year',
    commission: 'up to $150/client + additional perks',
    benefits: ['New Income Opportunity for your facility', 'Brand Awareness', 'Increase in Client retention', 'Co-branded Marketing Materials', 'Partner Portal access', 'Recruitment education sessions', 'Dedicated Account Manager', 'Complimentary Hotel Stay for 3 nights']
  }];
  const steps = [{
    number: '1',
    title: 'Apply',
    description: 'Submit your application with your training credentials and client base information.'
  }, {
    number: '2',
    title: 'Review',
    description: 'Our team reviews your application and schedules a discovery call within 48 hours.'
  }, {
    number: '3',
    title: 'Onboard',
    description: 'Complete partner training and receive your marketing materials and booking portal access.'
  }, {
    number: '4',
    title: 'Launch',
    description: 'Start promoting programs to your clients and earn commissions on every booking.'
  }];
  const handleProgramToggle = programId => {
    setExpandedProgram(expandedProgram === programId ? null : programId);
  };
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-2909">
      {/* Hero */}
      <section className="relative py-32 md:py-40 overflow-hidden" data-id="element-2910">
        <div className="absolute inset-0 z-0" data-id="element-2911">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1bXGSRJ9W7VoLhVjbdhEWf/HIIT.webp" alt="People exercising on a Cancun beach" className="w-full h-full object-cover" data-id="element-2912" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0099A1]/90 to-[#374047]/70" data-id="element-2913"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2914">
          <div className="max-w-3xl" data-id="element-2915">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-md" data-id="element-2916">
              Trainer Partner Program
            </h1>
            <p className="text-xl text-white/90 mb-8 drop-shadow-md" data-id="element-2917">
              Transform your training business by offering exclusive wellness
              retreats in Cancun. Earn competitive commissions while providing
              unforgettable experiences to your clients.
            </p>
            <div className="flex flex-wrap gap-4" data-id="element-2918">
              <Button variant="primary" size="lg" href="https://form.typeform.com/to/H1Cr2cAj" target="_blank" rel="noopener noreferrer" data-id="element-2919">
                Apply to Partner
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Modal */}
      {showApplicationForm && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-2920">
          <div className="bg-white rounded-lg max-w-2xl w-full my-8" data-id="element-2921">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-2922">
              <h2 className="text-2xl font-bold text-[#374047]" data-id="element-2923">
                Partner Application
              </h2>
              <button onClick={() => setShowApplicationForm(false)} className="text-gray-400 hover:text-gray-600" data-id="element-2924">
                <XIcon className="h-6 w-6" data-id="element-2925" />
              </button>
            </div>
            <form onSubmit={handleApplicationSubmit} className="p-6" data-id="element-2926">
              <div className="space-y-4" data-id="element-2927">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2928">
                  <div data-id="element-2929">
                    <label htmlFor="name" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-2930">
                      Full Name *
                    </label>
                    <input type="text" id="name" name="name" value={applicationData.name} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-2931" />
                  </div>
                  <div data-id="element-2932">
                    <label htmlFor="email" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-2933">
                      Email Address *
                    </label>
                    <input type="email" id="email" name="email" value={applicationData.email} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-2934" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2935">
                  <div data-id="element-2936">
                    <label htmlFor="phone" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-2937">
                      Phone Number *
                    </label>
                    <input type="tel" id="phone" name="phone" value={applicationData.phone} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-2938" />
                  </div>
                  <div data-id="element-2939">
                    <label htmlFor="businessName" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-2940">
                      Business/Brand Name *
                    </label>
                    <input type="text" id="businessName" name="businessName" value={applicationData.businessName} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-2941" />
                  </div>
                </div>
                <div data-id="element-2942">
                  <label htmlFor="businessType" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-2943">
                    Business Type *
                  </label>
                  <select id="businessType" name="businessType" value={applicationData.businessType} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-2944">
                    <option value="" data-id="element-2945">Select Business Type</option>
                    <option value="inperson" data-id="element-2946">In-Person Trainer</option>
                    <option value="virtual" data-id="element-2947">Virtual Trainer</option>
                    <option value="influencer" data-id="element-2948">Wellness Influencer</option>
                    <option value="club" data-id="element-2949">Health Club/Studio</option>
                  </select>
                </div>
                <div data-id="element-2950">
                  <label htmlFor="clientBase" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-2951">
                    Estimated Client Base Size *
                  </label>
                  <select id="clientBase" name="clientBase" value={applicationData.clientBase} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-2952">
                    <option value="" data-id="element-2953">Select Client Base Size</option>
                    <option value="small" data-id="element-2954">1-50 clients</option>
                    <option value="medium" data-id="element-2955">51-200 clients</option>
                    <option value="large" data-id="element-2956">201-500 clients</option>
                    <option value="xlarge" data-id="element-2957">500+ clients</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2958">
                  <div data-id="element-2959">
                    <label htmlFor="website" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-2960">
                      Website URL
                    </label>
                    <input type="url" id="website" name="website" value={applicationData.website} onChange={handleApplicationChange} placeholder="https://" className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-2961" />
                  </div>
                  <div data-id="element-2962">
                    <label htmlFor="instagram" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-2963">
                      Instagram Handle
                    </label>
                    <input type="text" id="instagram" name="instagram" value={applicationData.instagram} onChange={handleApplicationChange} placeholder="@yourusername" className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-2964" />
                  </div>
                </div>
                <div data-id="element-2965">
                  <label htmlFor="experience" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-2966">
                    Years of Experience *
                  </label>
                  <select id="experience" name="experience" value={applicationData.experience} onChange={handleApplicationChange} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-2967">
                    <option value="" data-id="element-2968">Select Experience Level</option>
                    <option value="1-2" data-id="element-2969">1-2 years</option>
                    <option value="3-5" data-id="element-2970">3-5 years</option>
                    <option value="6-10" data-id="element-2971">6-10 years</option>
                    <option value="10+" data-id="element-2972">10+ years</option>
                  </select>
                </div>
                <div data-id="element-2973">
                  <label htmlFor="message" className="block text-sm font-medium text-[#374047] mb-1" data-id="element-2974">
                    Why do you want to partner with DestaMeet?
                  </label>
                  <textarea id="message" name="message" value={applicationData.message} onChange={handleApplicationChange} rows={4} className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-2975"></textarea>
                </div>
              </div>
              <div className="mt-6 flex gap-4" data-id="element-2976">
                <Button type="submit" variant="primary" className="flex-1" data-id="element-2977">
                  Submit Application
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowApplicationForm(false)} className="flex-1" data-id="element-2978">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>}

      {/* Persona Selector */}
      <section className="py-16 bg-white" data-id="element-2979">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2980">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2981">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-2982">
              Choose Your Partner Type
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-2983">
              Select your role to see how the Trainer Partner Program works for
              you
            </p>
          </div>
          {/* Persona Tabs */}
          <div className="flex justify-center mb-12 overflow-x-auto" data-id="element-2984">
            <div className="inline-flex space-x-4 bg-[#F4E8D0]/30 p-2 rounded-lg" data-id="element-2985">
              {personas.map(persona => <button key={persona.id} onClick={() => setActivePersona(persona.id)} className={`flex flex-col items-center px-6 py-4 rounded-lg transition-colors ${activePersona === persona.id ? 'bg-[#0099A1] text-white' : 'text-[#374047] hover:bg-[#0099A1]/10'}`} data-id="element-2986">
                  <div className={`mb-2 ${activePersona === persona.id ? 'text-white' : ''}`} data-id="element-2987">
                    {persona.icon}
                  </div>
                  <span className="font-medium whitespace-nowrap" data-id="element-2988">
                    {persona.title}
                  </span>
                  <span className="text-xs mt-1 opacity-80" data-id="element-2989">
                    {persona.description}
                  </span>
                </button>)}
            </div>
          </div>
          {/* Active Persona Content */}
          <div className="max-w-5xl mx-auto" data-id="element-2990">
            <div className="text-center mb-12" data-id="element-2991">
              <h3 className="text-2xl md:text-3xl font-bold text-[#374047] mb-2" data-id="element-2992">
                {personaContent[activePersona].title}
              </h3>
              <p className="text-lg text-[#374047]/80" data-id="element-2993">
                {personaContent[activePersona].subtitle}
              </p>
            </div>
            {/* Benefits Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-2994">
              {personaContent[activePersona].benefits.map((benefit, index) => <Card key={index} data-id="element-2995">
                  <CardBody className="p-6" data-id="element-2996">
                    <div className="flex items-start" data-id="element-2997">
                      <div className="flex-shrink-0 mr-4" data-id="element-2998">{benefit.icon}</div>
                      <div data-id="element-2999">
                        <h4 className="text-lg font-bold text-[#374047] mb-2" data-id="element-3000">
                          {benefit.title}
                        </h4>
                        <p className="text-[#374047]/80" data-id="element-3001">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>)}
            </div>
            {/* Challenges vs Solutions */}
            <div className="grid md:grid-cols-2 gap-8" data-id="element-3002">
              <div data-id="element-3003">
                <h4 className="text-xl font-bold text-[#374047] mb-4" data-id="element-3004">
                  Common Challenges
                </h4>
                <div className="space-y-3" data-id="element-3005">
                  {personaContent[activePersona].challenges.map((challenge, index) => <div key={index} className="flex items-start bg-[#F4E8D0]/30 p-3 rounded-lg" data-id="element-3006">
                        <span className="text-[#FF6B65] mr-3" data-id="element-3007">✗</span>
                        <span className="text-[#374047]" data-id="element-3008">{challenge}</span>
                      </div>)}
                </div>
              </div>
              <div data-id="element-3009">
                <h4 className="text-xl font-bold text-[#374047] mb-4" data-id="element-3010">
                  Our Solutions
                </h4>
                <div className="space-y-3" data-id="element-3011">
                  {personaContent[activePersona].solutions.map((solution, index) => <div key={index} className="flex items-start bg-[#0099A1]/10 p-3 rounded-lg" data-id="element-3012">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] mr-3 flex-shrink-0 mt-0.5" data-id="element-3013" />
                        <span className="text-[#374047]" data-id="element-3014">{solution}</span>
                      </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Featured Programs */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-3015">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3016">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3017">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-3018">
              Featured Wellness Programs
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-3019">Vacation with Intention</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-3020">
            {programs.map((program, index) => <Card key={index} className="overflow-hidden" data-id="element-3021">
                <div className="h-64 overflow-hidden" data-id="element-3022">
                  <img src={program.image} alt={program.title} className="w-full h-full object-cover" data-id="element-3023" />
                </div>
                <CardBody className="p-6" data-id="element-3024">
                  <div className="mb-2" data-id="element-3025">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0099A1]/10 text-[#0099A1]" data-id="element-3026">
                      {program.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#374047] mb-2" data-id="element-3027">
                    {program.title}
                  </h3>
                  <div className="space-y-2 mb-4" data-id="element-3028">
                    <div className="flex items-center text-sm text-[#374047]/70" data-id="element-3029">
                      <CalendarIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-3030" />
                      {program.duration}
                    </div>
                    <div className="flex items-center text-sm text-[#374047]/70" data-id="element-3031">
                      <UsersIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-3032" />
                      {program.ideal}
                    </div>
                    <div className="flex items-center text-sm text-[#374047]/70" data-id="element-3033">
                      <DollarSignIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-3034" />
                      {program.price}
                    </div>
                  </div>
                  {/* Expandable Details */}
                  {expandedProgram === program.id && <div className="mt-4 pt-4 border-t border-[#374047]/10 space-y-4" data-id="element-3035">
                      <div data-id="element-3036">
                        <p className="text-sm text-[#374047]/80 mb-4" data-id="element-3037">
                          {program.description}
                        </p>
                      </div>
                      <div data-id="element-3038">
                        <h4 className="text-sm font-semibold text-[#374047] mb-2" data-id="element-3039">
                          Location
                        </h4>
                        <div className="flex items-center text-sm text-[#374047]/80" data-id="element-3040">
                          <MapPinIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-3041" />
                          {program.location}
                        </div>
                      </div>
                      <div data-id="element-3042">
                        <h4 className="text-sm font-semibold text-[#374047] mb-2" data-id="element-3043">
                          Upcoming Dates
                        </h4>
                        <div className="flex flex-wrap gap-2" data-id="element-3044">
                          {program.dates.map((date, i) => <span key={i} className="text-xs bg-[#0099A1]/10 text-[#0099A1] px-2 py-1 rounded" data-id="element-3045">
                              {date}
                            </span>)}
                        </div>
                      </div>
                      <div data-id="element-3046">
                        <h4 className="text-sm font-semibold text-[#374047] mb-2" data-id="element-3047">
                          Program Highlights
                        </h4>
                        <ul className="space-y-1" data-id="element-3048">
                          {program.highlights.map((highlight, i) => <li key={i} className="flex items-start text-sm text-[#374047]/80" data-id="element-3049">
                              <CheckIcon className="h-4 w-4 text-[#0099A1] mr-2 flex-shrink-0 mt-0.5" data-id="element-3050" />
                              <span data-id="element-3051">{highlight}</span>
                            </li>)}
                        </ul>
                      </div>
                      {program.hostImage && program.hostName && <div className="bg-[#F4E8D0]/30 p-3 rounded-lg" data-id="element-3052">
                          <h4 className="text-sm font-semibold text-[#374047] mb-2" data-id="element-3053">
                            Your Host
                          </h4>
                          <div className="flex items-start" data-id="element-3054">
                            <img src={program.hostImage} alt={program.hostName} className="w-12 h-12 rounded-full object-cover mr-3 flex-shrink-0" data-id="element-3055" />
                            <div data-id="element-3056">
                              <p className="font-semibold text-sm text-[#374047]" data-id="element-3057">
                                {program.hostName}
                              </p>
                              {program.hostBio && <p className="text-xs text-[#374047]/80 mt-1" data-id="element-3058">
                                  {program.hostBio}
                                </p>}
                            </div>
                          </div>
                        </div>}
                      <Button variant="primary" className="w-full" href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0LqS_WBaJU_UweOzewNSfk2vu7lxzFJN3nlB21e-yByPHLTO2p6nMafIxTmsxJzJbdXaw9qtoL" target="_blank" data-id="element-3059">
                        Book Appointment to Partner with Us
                      </Button>
                    </div>}
                  <Button variant="outline" className="w-full mt-4" onClick={() => handleProgramToggle(program.id)} data-id="element-3060">
                    {expandedProgram === program.id ? 'Show Less' : 'View Details'}
                  </Button>
                </CardBody>
              </Card>)}
          </div>
          <div className="text-center mt-8" data-id="element-3061">
            <Button variant="primary" href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0LqS_WBaJU_UweOzewNSfk2vu7lxzFJN3nlB21e-yByPHLTO2p6nMafIxTmsxJzJbdXaw9qtoL" target="_blank" data-id="element-3062">
              Book Appointment to Partner with Us
            </Button>
          </div>
        </div>
      </section>
      {/* Commission Structure */}
      <section className="py-16 bg-white" data-id="element-3063">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3064">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3065">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-3066">
              Competitive Commission Structure
            </h2>
            <p className="text-lg text-[#374047]/80 mb-2" data-id="element-3067">
              Earn more as you grow. Our tiered structure rewards your success.
            </p>
            <p className="text-base font-semibold text-[#0099A1]" data-id="element-3068">
              No financial investment required
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-3069">
            {commissionStructure.map((tier, index) => <Card key={index} className={`${index === 1 ? 'border-2 border-[#0099A1]' : ''}`} data-id="element-3070">
                <CardHeader className={`${index === 1 ? 'bg-[#0099A1] text-white' : 'bg-[#F4E8D0]/30'} p-6`} data-id="element-3071">
                  {index === 1 && <div className="text-center mb-2" data-id="element-3072">
                      <span className="inline-block bg-[#FF6B65] text-white text-xs px-3 py-1 rounded-full" data-id="element-3073">
                        Most Popular
                      </span>
                    </div>}
                  <h3 className={`text-2xl font-bold ${index === 1 ? 'text-white' : 'text-[#374047]'} text-center`} data-id="element-3074">
                    {tier.tier}
                  </h3>
                  <p className={`text-center ${index === 1 ? 'text-white/80' : 'text-[#374047]/70'} mt-2`} data-id="element-3075">
                    {tier.bookings}
                  </p>
                  <div className="text-center mt-4" data-id="element-3076">
                    <span className={`text-3xl font-bold ${index === 1 ? 'text-white' : 'text-[#0099A1]'}`} data-id="element-3077">
                      {tier.commission}
                    </span>
                  </div>
                </CardHeader>
                <CardBody className="p-6" data-id="element-3078">
                  <ul className="space-y-3" data-id="element-3079">
                    {tier.benefits.map((benefit, i) => <li key={i} className="flex items-start" data-id="element-3080">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5 mr-2" data-id="element-3081" />
                        <span className="text-[#374047]" data-id="element-3082">{benefit}</span>
                      </li>)}
                  </ul>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>
      {/* How It Works */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-3083">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3084">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3085">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-3086">
              How It Works
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-3087">
              Get started in four simple steps
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto" data-id="element-3088">
            {steps.map((step, index) => <div key={index} className="text-center" data-id="element-3089">
                <div className="w-16 h-16 bg-[#0099A1] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4" data-id="element-3090">
                  {step.number}
                </div>
                <h3 className="text-xl font-bold text-[#374047] mb-2" data-id="element-3091">
                  {step.title}
                </h3>
                <p className="text-[#374047]/80" data-id="element-3092">{step.description}</p>
              </div>)}
          </div>
        </div>
      </section>
      {/* Partner Benefits */}
      <section className="py-16 bg-white" data-id="element-3093">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3094">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3095">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-3096">
              Why Partner With DestaMeet?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-3097">
            <div className="text-center" data-id="element-3098">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3099">
                <ClipboardCheckIcon className="h-8 w-8 text-[#0099A1]" data-id="element-3100" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-3101">
                Turnkey Operations
              </h3>
              <p className="text-[#374047]/80" data-id="element-3102">
                We handle all logistics, programming, and execution. You focus
                on your clients.
              </p>
            </div>
            <div className="text-center" data-id="element-3103">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3104">
                <DollarSignIcon className="h-8 w-8 text-[#0099A1]" data-id="element-3105" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-3106">
                Competitive Commissions
              </h3>
              <p className="text-[#374047]/80" data-id="element-3107">
                Earn up to 20% commission on all bookings with our tiered
                structure.
              </p>
            </div>
            <div className="text-center" data-id="element-3108">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3109">
                <StarIcon className="h-8 w-8 text-[#0099A1]" data-id="element-3110" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-3111">
                Premium Experiences
              </h3>
              <p className="text-[#374047]/80" data-id="element-3112">
                Offer world-class wellness programs at luxury resorts in Cancun.
              </p>
            </div>
            <div className="text-center" data-id="element-3113">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3114">
                <HandshakeIcon className="h-8 w-8 text-[#0099A1]" data-id="element-3115" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-3116">
                Dedicated Support
              </h3>
              <p className="text-[#374047]/80" data-id="element-3117">
                Your own account manager to help you succeed every step of the
                way.
              </p>
            </div>
            <div className="text-center" data-id="element-3118">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3119">
                <BookOpenIcon className="h-8 w-8 text-[#0099A1]" data-id="element-3120" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-3121">
                Marketing Resources
              </h3>
              <p className="text-[#374047]/80" data-id="element-3122">
                Professional marketing materials and co-branded content to
                promote programs.
              </p>
            </div>
            <div className="text-center" data-id="element-3123">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3124">
                <GlobeIcon className="h-8 w-8 text-[#0099A1]" data-id="element-3125" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-3126">
                Partner Portal
              </h3>
              <p className="text-[#374047]/80" data-id="element-3127">
                Easy-to-use booking system and dashboard to track your
                commissions.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 bg-[#0099A1]" data-id="element-3128">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3129">
          <div className="max-w-3xl mx-auto text-center" data-id="element-3130">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-3131">
              Ready to Transform Your Training Business?
            </h2>
            <p className="text-xl text-white/90 mb-8" data-id="element-3132">
              Join our network of elite fitness professionals and start offering
              unforgettable wellness experiences to your clients.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-3133">
              <Button variant="primary" size="lg" href="https://form.typeform.com/to/H1Cr2cAj" target="_blank" rel="noopener noreferrer" data-id="element-3134">
                Apply Now
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10" href="https://calendar.app.google/4vusYaEgW5mhzbSV9" target="_blank" data-id="element-3135">
                Schedule a Call
              </Button>
            </div>
            <p className="text-sm text-white/70 mt-6" data-id="element-3136">
              Applications reviewed within 48 hours
            </p>
          </div>
        </div>
      </section>
      {/* Program Detail Modal */}
      {selectedProgram && <ProgramDetailModal program={selectedProgram} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data-id="element-3137" />}
    </div>;
}